import React from 'react';
import {
  ApplianceShell,
  ControlBand,
  HorizontalHandle,
  HorizontalSplit,
  ToeKick,
  VerticalSplit,
  TOE_KICK_HEIGHT_M,
} from './ApplianceFrame';

const INCHES_TO_METERS = 0.0254;

/**
 * 42" — matches RANGE_PRO_WIDTH_THRESHOLD in
 * shared-lib/kitchen-assembly/materializeCandidate.js and
 * STOVE_PRO_WIDTH_THRESHOLD in the 3D applianceRenderers. At and above
 * this width we draw a 6-burner + griddle pro layout instead of a
 * 4-burner residential layout. The width-driven split is intentional
 * even when `subtype === 'freestanding'`.
 */
const RANGE_PRO_WIDTH_THRESHOLD_M = 42 * INCHES_TO_METERS;

const CONTROL_BAND_RATIO = 0.1;

/**
 * Resolve which range variant to render. `subtype` is the canonical
 * field emitted by materializeCandidate (`'slide-in'` | `'freestanding'`).
 * `drop-in` is supported per the plan but is not currently emitted by
 * the kitchen pipeline — callers can pass it explicitly.
 */
export function resolveRangeVariant(obj = {}) {
  const subtype = obj.subtype;
  if (subtype === 'drop-in') return 'drop-in';
  if (subtype === 'freestanding') return 'freestanding';
  return 'slide-in';
}

export function RangeSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters,
  obj = {},
  strokeColor,
  strokeWidth,
  view = 'front',
}) {
  if (view === 'left' || view === 'right') {
    return (
      <RangeSideView
        view={view}
        depthMeters={depthMeters}
        heightMeters={height}
        obj={obj}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    );
  }
  return (
    <RangeFrontView
      width={width}
      height={height}
      obj={obj}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    />
  );
}

RangeSchematic.supportsSideViews = true;

function RangeFrontView({ width, height, obj, strokeColor, strokeWidth }) {
  const variant = resolveRangeVariant(obj);
  const isPro = width >= RANGE_PRO_WIDTH_THRESHOLD_M;
  const hasToeKick = variant !== 'drop-in';

  const bandH = height * CONTROL_BAND_RATIO;
  const toeY = hasToeKick ? height - TOE_KICK_HEIGHT_M : height;
  const knobCount = isPro ? 7 : variant === 'drop-in' ? 0 : 4;

  return (
    <ApplianceShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      <ControlBand
        width={width}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
        ratio={CONTROL_BAND_RATIO}
        knobCount={knobCount}
      />
      {hasToeKick && (
        <ToeKick
          width={width}
          height={height}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      )}
      {isPro ? (
        <ProOvenSplit
          width={width}
          bandH={bandH}
          toeY={toeY}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      ) : (
        <StandardOvenDoor
          width={width}
          bandH={bandH}
          toeY={toeY}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      )}
    </ApplianceShell>
  );
}

/**
 * Side elevation of a range: rectangular body (depth × height) with
 * a slight back-splash bump at top rear, an oven-door handle bar
 * across the front edge near the top of the oven door, and a small
 * knob cluster on the control panel at the top. The control panel,
 * handle, and back-splash all reference the FRONT edge — the side
 * view is asymmetric, so LEFT vs RIGHT are mirrored.
 *
 * Convention: in the LEFT view the front face points to the RIGHT
 * (i.e. x = depthMeters is the front edge), so the back-splash bump
 * sits at x = 0 and the handle/knob protrude near x = depthMeters.
 * The RIGHT view wraps in a scale(-1, 1) mirror.
 */
function RangeSideView({
  depthMeters,
  heightMeters,
  obj,
  strokeColor,
  strokeWidth,
  view,
}) {
  const w = depthMeters;
  const h = heightMeters;
  const variant = resolveRangeVariant(obj);
  const hasToeKick = variant !== 'drop-in';
  // The control band sits between the cooktop and the oven door, so
  // its TOP is the cooktop deck and its BOTTOM is where the oven door
  // begins. We carve out a 2"-tall cooktop band at the top of the body
  // and split the control band off below it.
  const cooktopH = Math.min(h * 0.04, 0.05);
  const bandTopY = cooktopH;
  const bandBottomY = bandTopY + h * CONTROL_BAND_RATIO;
  const toeY = hasToeKick ? h - TOE_KICK_HEIGHT_M : h;

  // Back-splash: small bump along the top-rear corner. Freestanding
  // ranges have it; slide-in / drop-in don't (cooktop is flush).
  const hasBacksplash = variant === 'freestanding';
  const backsplashW = Math.min(w * 0.18, 0.06);
  const backsplashH = hasBacksplash ? Math.min(h * 0.05, 0.05) : 0;

  // Handle bar near the top of the oven door, projecting from the
  // front edge (x = w). Make it tall enough to read at the strip's
  // ~110px height.
  const ovenDoorH = toeY - bandBottomY;
  const handleProtrusion = Math.min(w * 0.06, 0.025);
  const handleY = bandBottomY + ovenDoorH * 0.14;
  const handleLen = Math.max(ovenDoorH * 0.1, 0.025);

  // Knob: small forward-protrusion at the front edge of the control
  // band (knobs project forward in real life, so in a side projection
  // they're a tiny bump at x=w, not a circle inside the body).
  const knobR = Math.min(bandBottomY - bandTopY, 0.012) * 0.5;
  const knobCy = (bandTopY + bandBottomY) / 2;

  const sideChildren = (
    <>
      {/* Cooktop divider line — separates the deck from the body. */}
      <HorizontalSplit
        width={w}
        y={cooktopH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Control band horizontal splits — top and bottom edges. */}
      <HorizontalSplit
        width={w}
        y={bandBottomY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Back-splash bump at top rear (rear = x:0). Freestanding only. */}
      {hasBacksplash && (
        <>
          <line
            x1={0}
            y1={-backsplashH}
            x2={backsplashW}
            y2={-backsplashH}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            vectorEffect="non-scaling-stroke"
          />
          <line
            x1={backsplashW}
            y1={-backsplashH}
            x2={backsplashW}
            y2={0}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            vectorEffect="non-scaling-stroke"
          />
        </>
      )}
      {/* Knob protrusion at front edge of control band. */}
      <circle
        cx={w + knobR}
        cy={knobCy}
        r={knobR}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
      {/* Toe-kick recess line */}
      {hasToeKick && (
        <line
          x1={0}
          y1={toeY}
          x2={w}
          y2={toeY}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          vectorEffect="non-scaling-stroke"
        />
      )}
      {/* Oven-door handle bar projecting from front edge as a U. */}
      <line
        x1={w}
        y1={handleY - handleLen / 2}
        x2={w + handleProtrusion}
        y2={handleY - handleLen / 2}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
      <line
        x1={w + handleProtrusion}
        y1={handleY - handleLen / 2}
        x2={w + handleProtrusion}
        y2={handleY + handleLen / 2}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
      <line
        x1={w + handleProtrusion}
        y1={handleY + handleLen / 2}
        x2={w}
        y2={handleY + handleLen / 2}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
    </>
  );

  return (
    <svg
      viewBox={`0 ${-backsplashH} ${w} ${h + backsplashH}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ width: '100%', height: '100%', overflow: 'visible' }}
    >
      <rect
        x={0}
        y={0}
        width={w}
        height={h}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      {view === 'right' ? (
        <g transform={`translate(${w}, 0) scale(-1, 1)`}>{sideChildren}</g>
      ) : (
        sideChildren
      )}
    </svg>
  );
}

/**
 * Standard residential range: single oven door spanning the full
 * width, horizontal handle near the top of the door.
 */
function StandardOvenDoor({ width, bandH, toeY, strokeColor, strokeWidth }) {
  const handleY = bandH + (toeY - bandH) * 0.12;
  return (
    <HorizontalHandle
      cx={width / 2}
      y={handleY}
      length={width * 0.7}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    />
  );
}

/**
 * Pro range: oven door split ~66/34 (matches the 3D pro variant in
 * applianceRenderers — wide left oven + narrow right oven). Each door
 * gets its own handle near the top.
 */
function ProOvenSplit({ width, bandH, toeY, strokeColor, strokeWidth }) {
  const LEFT_RATIO = 0.66;
  const splitX = width * LEFT_RATIO;
  const handleY = bandH + (toeY - bandH) * 0.12;
  return (
    <g>
      <VerticalSplit
        x={splitX}
        y1={bandH}
        y2={toeY}
        height={toeY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <HorizontalHandle
        cx={splitX / 2}
        y={handleY}
        length={splitX * 0.75}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <HorizontalHandle
        cx={splitX + (width - splitX) / 2}
        y={handleY}
        length={(width - splitX) * 0.75}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}
